import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import CustomerAdd from './CustomerAdd';
import InvoiceAdd from './InvoiceAdd';
import PaymentAdd from './PaymentAdd';
import CustomerEdit from './CustomerEdit';
import CustomerList from './CustomerList';
import { motion } from 'framer-motion';
import ExpenseList from './ExpenseList';

function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showCustomerListModal, setShowCustomerListModal] = useState(false);
  const [selectedCustomerToEdit, setSelectedCustomerToEdit] = useState(null);
  const [refreshDashboardKey, setRefreshDashboardKey] = useState(0);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [exchangeRates, setExchangeRates] = useState(null);

  // Login kontrolü için useEffect kullan!
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Esc tuşu ile modal kapatma
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowCustomerModal(false);
        setShowInvoiceModal(false);
        setShowPaymentModal(false);
        setShowEditCustomerModal(false);
        setShowCustomerListModal(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Dashboard verisi çekme
  useEffect(() => {
    if (user) {
      fetch(`https://www.kenan.bybello.com/backend/api/summary.php?user_id=${user.id}`, {
        credentials: 'include',
      })
        .then(res => {
          if (!res.ok) {
            throw new Error('Dashboard verileri çekilemedi.');
          }
          return res.json();
        })
        .then(data => setSummary(data))
        .catch(error => console.error("Dashboard verileri çekilirken hata oluştu:", error));
    }
  }, [user, refreshDashboardKey]);

  // Döviz kuru çekme (frankfurter.app)
  useEffect(() => {
    fetch('https://api.frankfurter.app/latest?from=TRY&to=USD,EUR')
      .then(res => res.json())
      .then(data => setExchangeRates(data.rates))
      .catch(() => setExchangeRates(null));
  }, []);

  // Yüklenme kontrolü
  if (!user || !summary) return <div>Yükleniyor...</div>;

  const handleRefreshAndCloseModal = () => {
    setShowCustomerModal(false);
    setShowInvoiceModal(false);
    setShowPaymentModal(false);
    setShowEditCustomerModal(false);
    setShowCustomerListModal(false);
    setShowExpenseModal(false);
    setRefreshDashboardKey(prevKey => prevKey + 1);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEditClick = (customer) => {
    setSelectedCustomerToEdit(customer);
    setShowEditCustomerModal(true);
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Yönetim Paneli</h1>
        <button className="btn btn-danger" onClick={handleLogout}>Çıkış Yap</button>
      </div>
      <div className="alert alert-primary">Hoş geldiniz, SN. {user.company_name}</div>
      <div className="alert alert-info mb-3">
        <b>Güncel Döviz Kurları:</b>
        {exchangeRates ? (
          <span style={{ marginLeft: 10 }}>
            1 USD = {(1 / exchangeRates.USD).toFixed(2)} TL, 1 EUR = {(1 / exchangeRates.EUR).toFixed(2)} TL
          </span>
        ) : (
          <span style={{ marginLeft: 10 }}>Döviz bilgisi alınamadı.</span>
        )}
      </div>

      <h2>Kasa Durumu</h2>
      <div className="row mb-4">
        <div className="col-md-3">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="card text-white bg-success mb-3"
          >
            <div className="card-header">Alınan Para</div>
            <div className="card-body">
              <h4 className="card-title">₺{Number(summary.total_payments).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</h4>
            </div>
          </motion.div>
        </div>
        <div className="col-md-3">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="card text-white bg-warning mb-3"
          >
            <div className="card-header">Dışarıdaki Para (Alacaklar)</div>
            <div className="card-body">
              <h4 className="card-title">₺{Number(summary.total_receivables).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</h4>
            </div>
          </motion.div>
        </div>
        <div className="col-md-3">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="card text-white bg-danger mb-3"
          >
            <div className="card-header">Toplam Harcamalar</div>
            <div className="card-body">
              <h4 className="card-title">₺{Number(summary.total_expenses).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</h4>
            </div>
          </motion.div>
        </div>
        <div className="col-md-3">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="card text-white bg-dark mb-3"
          >
            <div className="card-header">Kasa (Net Bakiye)</div>
            <div className="card-body">
              <h4 className="card-title">₺{Number(summary.net_cash).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</h4>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-3">
          <button className="btn btn-primary w-100" style={{ minHeight: 56, fontSize: 20 }} onClick={() => setShowCustomerModal(true)}>
          Yeni Müşteri Ekle
        </button>
        </div>
        <div className="col-md-3">
          <button className="btn btn-secondary w-100" style={{ minHeight: 56, fontSize: 20 }} onClick={() => setShowCustomerListModal(true)}>
          Müşteriler
        </button>
        </div>
        <div className="col-md-3">
          <button className="btn btn-info w-100" style={{ minHeight: 56, fontSize: 20 }} onClick={() => setShowInvoiceModal(true)}>
          İrsaliye Kes
        </button>
        </div>
        <div className="col-md-3">
          <button className="btn btn-warning w-100" style={{ minHeight: 56, fontSize: 20 }} onClick={() => setShowPaymentModal(true)}>
          Ödeme Al / Müşteri Detay
        </button>
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-md-3 offset-md-9">
          <button className="btn btn-dark w-100" style={{ minHeight: 56, fontSize: 20 }} onClick={() => setShowExpenseModal(true)}>
          Harcamalar
        </button>
        </div>
      </div>

      <h2>Son İşlemler</h2>
      <div className="row">
        <div className="col-md-4">
          <h5>Son Müşteriler</h5>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Adı</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {summary.recent_customers.length === 0 ? (
                <tr><td colSpan="2">Henüz müşteri yok.</td></tr>
              ) : (
                summary.recent_customers.map((c, i) => (
                  <tr key={c.id}>
                    <td><Link to={`/customer/${c.id}`}>{c.name}</Link></td>
                    <td>
                      <button onClick={() => navigate(`/customer/${c.id}`)} className="btn btn-info btn-sm">Müşteri Detay</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="col-md-4">
          <h5>Son İrsaliyeler</h5>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>İrsaliye No</th>
                <th>Tutar</th>
              </tr>
            </thead>
            <tbody>
              {summary.recent_invoices.length === 0 ? (
                <tr><td colSpan="2">Henüz irsaliye yok.</td></tr>
              ) : (
                summary.recent_invoices.map((i, idx) => (
                  <tr key={idx}>
                    <td>{i.invoice_number}</td>
                    <td>{Number(i.total_price).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="col-md-4">
          <h5>Son Ödemeler</h5>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Müşteri Adı</th>
                <th>Tutar</th>
              </tr>
            </thead>
            <tbody>
              {summary.recent_payments.length === 0 ? (
                <tr><td colSpan="2">Henüz ödeme yok.</td></tr>
              ) : (
                summary.recent_payments.map((p, idx) => (
                  <tr key={idx}>
                    <td>{p.customer_name}</td>
                    <td>{Number(p.amount).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCustomerModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Yeni Müşteri Ekle</h5>
                  <button type="button" className="btn-close" onClick={() => setShowCustomerModal(false)}></button>
                </div>
                <div className="modal-body">
                  <CustomerAdd onCustomerAdded={handleRefreshAndCloseModal} userId={user.id} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showInvoiceModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">İrsaliye Kes</h5>
                  <button type="button" className="btn-close" onClick={() => setShowInvoiceModal(false)}></button>
                </div>
                <div className="modal-body">
                  <InvoiceAdd onInvoiceAdded={handleRefreshAndCloseModal} userId={user.id} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showPaymentModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Ödeme Al</h5>
                  <button type="button" className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
                </div>
                <div className="modal-body">
                  <PaymentAdd onPaymentAdded={handleRefreshAndCloseModal} userId={user.id} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showEditCustomerModal && selectedCustomerToEdit && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Müşteri Düzenle</h5>
                  <button type="button" className="btn-close" onClick={() => setShowEditCustomerModal(false)}></button>
                </div>
                <div className="modal-body">
                  <CustomerEdit
                    customerData={selectedCustomerToEdit}
                    userId={user.id}
                    onCustomerUpdated={handleRefreshAndCloseModal}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showCustomerListModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Müşteriler</h5>
                  <button type="button" className="btn-close" onClick={() => setShowCustomerListModal(false)}></button>
                </div>
                <div className="modal-body">
                  <CustomerList refresh={refreshDashboardKey} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showExpenseModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Harcamalar</h5>
                  <button type="button" className="btn-close" onClick={() => setShowExpenseModal(false)}></button>
                </div>
                <div className="modal-body">
                  <ExpenseList onClose={() => setShowExpenseModal(false)} onExpenseChanged={handleRefreshAndCloseModal} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
